import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { Signature } from '../../models/Signature'
import { getViewTitleFromView } from '../../utils/dashboard'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import Tooltip from '../app/design/Tooltip'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'

type Props = {
    contracts: Signature[]
    view: number
    dashboard?: Dashboard
    searchText: string
    isLoading: boolean
}

const getTitleFromView = (view: number) => {
    if (view === 0 || view === 5) return 'Signed'
    if (view === 1) return 'Sent'
    if (view === 2) return 'Viewed'
    if (view === 3) return 'Cancelled'
    if (view === 4) return 'Signed'
    return 'Created'
}

const getDateFromView = (contract: Signature, view: number) => {
    if (view === 1) return contract.sentDate
    if (view === 2) return contract.viewedDate
    if (view === 3 || view === 4) return contract.cancelledDate
    if (view === 6) return contract.createdDate
    return contract.signedDate
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.all(
        base.props.contracts.map((c) => RecordFactory.getInstance().loadSignature(c.id!))
    )
}

const render = ({
    base: {
        props: { contracts, dashboard, searchText, view, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: `${getViewTitleFromView(dashboard, view)} ${
                            contracts.length === 1 ? `contract` : `contracts`
                        }`,
                        value: contracts.length.toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Contract</TableHead>
                        {view !== 4 && (
                            <TableHead className="font-normal">{getTitleFromView(view)}</TableHead>
                        )}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {contracts.map((c) => {
                        return (
                            <ClickableTableRow key={c.id} href={`/contracts/${c.id}`}>
                                <TableCell>
                                    <ClientTableCell user={c.person} subaccount={c.subaccount} />
                                </TableCell>
                                <TableCell>
                                    <Tooltip label={c.contract?.name} wrap position={'top'}>
                                        <Paragraph
                                            size="sm"
                                            className="max-w-[250px] truncate lg:max-w-[500px]"
                                        >
                                            {c.contract?.name}
                                        </Paragraph>
                                    </Tooltip>
                                </TableCell>
                                {view !== 4 && (
                                    <TableCell>
                                        <Paragraph size="sm" className="whitespace-nowrap">
                                            {getDateFromView(c, view)?.format('MMM D, YYYY')}
                                        </Paragraph>
                                    </TableCell>
                                )}
                            </ClickableTableRow>
                        )
                    })}
                    {contracts.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>
        </div>
    )
}

export const ContractsDashboard = usePSComponent(render, viewWillAppear)
